import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, MoreVertical, Maximize2, Minimize2, X } from 'lucide-react';

import useCsWebSocket from '@/hooks/useCsWebSocket';
import { getChatHistoryApi, closeChatApi, markReadApi } from '@/api/csChat';
import type { ChatLogItem } from '@/api/csChat';
import { uploadCsFile } from '@/api/csChat';
import type { Attachment } from '@/api/chat';
import { compressImage } from '@/utils/imageCompress';
import { compressVideo } from '@/utils/videoCompress';
import { getCsUserInfo } from '@/utils/store';
import sendIcon from '@/assets/icons/chat/send.svg';
import sendFailedIcon from '@/assets/icons/chat/send-failed-icon.svg';
import chatAddIcon from '@/assets/icons/csChat/chat_add.svg';
import chatEmojiIcon from '@/assets/icons/csChat/chat_emoji.svg';
import backIcon from '@/assets/icons/csChat/back.svg';
import defaultAvatar from '@/assets/icons/common/default_staff_logo.svg';
import defaultAssistantLogo from '@/assets/icons/common/default_assisant_logo.svg';
import styles from './index.module.css';

// ==================== 常量 ====================

const MAX_CHAR_LIMIT = 4000;
const MAX_IMAGE_COUNT = 3;

/** 统计已占用附件配额的媒体数量（图片+视频，含压缩/上传中的占位项） */
function countMediaAttachments(list: Attachment[]): number {
  return list.filter((a) => a.type === 'image' || a.type === 'video').length;
}

/** 格式化时间（秒级时间戳→展示字符串，如 16:27） */
function formatTime(createTime: number): string {
  const date = new Date(createTime * 1000);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours}:${String(minutes).padStart(2, '0')}`;
}

/** 判断两个时间戳是否在同一天 */
function isSameDay(t1: number, t2: number): boolean {
  const d1 = new Date(t1 * 1000);
  const d2 = new Date(t2 * 1000);
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

/** 判断两个时间戳是否超过1小时 */
function isOverOneHour(t1: number, t2: number): boolean {
  return Math.abs(t1 - t2) >= 3600;
}

/** 获取时间分隔条文本（根据消息时间与当前时间的差值决定显示格式） */
function getTimeSeparatorText(createTime: number): string {
  const now = new Date();
  const date = new Date(createTime * 1000);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;
  const sevenDaysAgo = todayStart - 6 * 24 * 60 * 60 * 1000;
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const timeStr = `${hours}:${minutes}`;
  const msgTime = date.getTime();
  if (msgTime >= todayStart) {
    return timeStr;
  }
  if (msgTime >= yesterdayStart) {
    return `昨天 ${timeStr}`;
  }
  if (msgTime >= sevenDaysAgo) {
    const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
    return `周${weekdays[date.getDay()]} ${timeStr}`;
  }
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${timeStr}`;
}

/**
 * 获取已读/未读标签（客服端：判断当前客服是否已读用户发的消息）
 * lastReadTime = 当前客服最后已读时间，用户消息的createTime <= lastReadTime 表示已读
 */
function getReadStatus(msg: ChatLogItem, lastReadTime: number | null): 'read' | 'unread' | 'none' {
  // 仅对用户发的消息显示已读/未读
  if (msg.role !== 'user') return 'none';
  if (lastReadTime == null) return 'unread';
  if (msg.createTime <= lastReadTime) return 'read';
  return 'unread';
}

/** 获取客服头像URL */
function getStaffAvatarUrl(): string {
  const info = getCsUserInfo();
  return info?.avatar || defaultAvatar;
}

/** 渲染multipart消息内容（图片在上，文字在下） */
function renderMultipartContent(
  parts: string | null,
  fallback: string | null,
  onPreview?: (url: string, type: 'image' | 'video') => void,
) {
  if (!parts) return <span>{fallback}</span>;
  try {
    // 后端 parts 字段可能使用驼峰命名（imageUrl/videoUrl）或下划线命名（image_url/video_url），统一兼容
    const rawList = JSON.parse(parts) as Array<{ type: string; text?: string; image_url?: { url: string }; video_url?: { url: string }; imageUrl?: { url: string }; videoUrl?: { url: string } }>;
    const partsList = rawList.map(p => {
      if (p.type === 'image_url' && p.imageUrl && !p.image_url) {
        return { ...p, image_url: p.imageUrl };
      }
      if (p.type === 'video_url' && p.videoUrl && !p.video_url) {
        return { ...p, video_url: p.videoUrl };
      }
      return p;
    }) as Array<{ type: string; text?: string; image_url?: { url: string }; video_url?: { url: string } }>;
    
    // 拆分为图片、视频、文字三组
    const images = partsList.filter(p => p.type === 'image_url' && p.image_url);
    const videos = partsList.filter(p => p.type === 'video_url' && p.video_url);
    const texts = partsList.filter(p => p.type === 'text' && p.text);
    
    return (
      <>
        {/* 图片网格（在上） */}
        {images.length > 0 && (
          <div className={styles.msgImageGrid}>
            {images.map((part, i) => (
              <div key={`img-${i}`} className={styles.msgImageGridItem}>
                <img
                  src={part.image_url!.url}
                  alt=""
                  className={styles.msgGridImage}
                  loading="lazy"
                  onClick={() => onPreview?.(part.image_url!.url, 'image')}
                />
              </div>
            ))}
          </div>
        )}
        
        {/* 视频网格（在上） */}
        {videos.length > 0 && (
          <div className={styles.msgVideoGrid}>
            {videos.map((part, i) => (
              <div key={`vid-${i}`} className={styles.msgVideoGridItem} onClick={() => onPreview?.(part.video_url!.url, 'video')}>
                <video src={`${part.video_url!.url}#t=0.1`} className={styles.msgGridVideo} preload="auto" muted playsInline onLoadedMetadata={(e) => { e.currentTarget.currentTime = 0.1; }} />
                <div className={styles.playIconOverlay}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><polygon points="8,5 19,12 8,19" /></svg>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* 文字（在下） */}
        {texts.map((part, i) => (
          <span key={`txt-${i}`}>{part.text}</span>
        ))}
      </>
    );
  } catch {
    return <span>{fallback}</span>;
  }
}

// ==================== 页面组件 ====================

export default function CsChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t: rawT } = useTranslation('chat');
  const t = rawT as (key: string, options?: Record<string, unknown>) => string;

  // 从路由state或URL参数获取humanChatId和用户信息
  const state = location.state as {
    humanChatId?: string;
    userNickname?: string;
    userAvatar?: string;
    chatStatus?: number;
    fromTab?: string;
  } | null;
  const humanChatId = state?.humanChatId || '';
  const userNickname = state?.userNickname || '用户';
  const userAvatar = state?.userAvatar || '';
  const chatStatus = state?.chatStatus;
  const fromTab = state?.fromTab;

  /** 返回客服工作台，携带来源 tab 以便定位 */
  const handleBack = () => {
    navigate('/csHome', {
      state: { returnTab: fromTab },
      replace: true,
    });
  };

  const currentSysUserId = getCsUserInfo()?.userId || 0;
  const staffAvatar = getStaffAvatarUrl();

  const [messages, setMessages] = useState<ChatLogItem[]>([]);
  const [lastReadTime, setLastReadTime] = useState<number | null>(null);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** StrictMode 防护：标记是否已执行过初始加载，防止开发环境双触发 */
  const initialLoadedRef = useRef(false);

  // ==================== 分页相关状态 ====================
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // 滚动管理 refs（用于滚动事件回调，避免闭包过期）
  const shouldScrollToBottomRef = useRef(false);
  const pendingScrollRestoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const loadingMoreRef = useRef(false);
  const hasMoreHistoryRef = useRef(true);
  const loadingRef = useRef(false);

  // 同步 ref 值
  loadingMoreRef.current = loadingMore;
  hasMoreHistoryRef.current = hasMoreHistory;
  loadingRef.current = loading;

  // 输入框复合状态
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 文件处理互斥锁：压缩/上传期间禁止再次选择，避免并发批次按旧快照算配额导致总数超限 */
  const uploadingRef = useRef(false);

  // 全屏预览
  const [previewFile, setPreviewFile] = useState<{ url: string; type: 'image' | 'video' } | null>(null);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  // ==================== 消息重试相关 ====================
  // 存储失败消息的重试数据（messageId -> 原始发送参数）
  const retryDataMapRef = useRef<Map<string, { text: string; parts: Array<{ type: string; text?: string; image_url?: { url: string }; video_url?: { url: string } }> | null; contentType: 'text' | 'multipart' }>>(new Map());
  // 当前正在重试的消息 ID
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);

  /** 显示 Toast 提示（2.5秒后自动消失） */
  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMsg(null), 2500);
  }, []);

  // WebSocket连接
  const ws = useCsWebSocket({
    onUserMsg: (data) => {
      if (data.humanChatId === humanChatId) {
        // 检查是否已存在相同ID的消息，避免重复渲染
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === data.chatLogId);
          if (exists) return prev;
          const newMsg: ChatLogItem = {
            id: data.chatLogId,
            chatId: '',
            role: 'user',
            contentType: data.contentType,
            content: data.content,
            parts: data.parts,
            isCurrentStaff: false,
            createTime: Date.now() / 1000,
          };
          return [...prev, newMsg];
        });
        // 收到用户消息后滚动到底部并标记已读
        shouldScrollToBottomRef.current = true;
        ws.sendStaffRead(humanChatId);
      }
    },
    onChatClosed: (data) => {
      if (data.humanChatId === humanChatId) {
        // 优先使用后端推送的 messageKey 解析本地化文本，降级到 message，最后按 endType 降级
        let content = data.message || '';
        if (data.messageKey) {
          const resolved = t(`ws.${data.messageKey}`);
          if (resolved && resolved !== `ws.${data.messageKey}`) {
            content = resolved;
          }
        }
        if (!content) {
          // 按 endType 降级：2=超时, 3=用户关闭, 1=客服关闭
          if (data.endType === 2) {
            content = t('ws.humanChatTimeout');
          } else {
            content = t('ws.humanChatEnded');
          }
        }
        const sysMsg: ChatLogItem = {
          id: `sys-${Date.now()}`,
          chatId: '',
          role: 'system',
          contentType: 'text',
          content,
          isCurrentStaff: false,
          createTime: Date.now() / 1000,
          displayType: 'split',
        };
        setMessages((prev) => [...prev, sysMsg]);
        shouldScrollToBottomRef.current = true;
      }
    },
    onReadUpdate: (data) => {
      if (data.humanChatId === humanChatId) {
        setLastReadTime(Date.now() / 1000);
      }
    },
  });

  /** 加载历史消息（支持分页：首页定位最新消息，向上滑动加载更早历史） */
  const loadHistory = useCallback(async (page: number = 1, isInitial: boolean = false) => {
    if (!humanChatId) return;
    if (isInitial) {
      setLoading(true);
      setHistoryPage(1);
      setHasMoreHistory(true);
    } else {
      if (loadingMoreRef.current || !hasMoreHistoryRef.current) return;
      // 分页加载前保存滚动位置，加载完成后恢复（避免窗口跳动）
      const container = mainRef.current;
      if (container) {
        pendingScrollRestoreRef.current = {
          scrollHeight: container.scrollHeight,
          scrollTop: container.scrollTop,
        };
      }
      setLoadingMore(true);
    }
    try {
      if (isInitial) {
        const readRes = await markReadApi({ humanChatId });
        setLastReadTime(readRes?.lastReadTime ?? null);
      }

      const res = await getChatHistoryApi(humanChatId, page, 20);
      if (res?.records) {
        // 后端 getChatHistory 改为 orderByDesc 降序排序
        // 前端需要反转数组，使消息按时间升序排列（早的消息在前，晚的消息在后）
        const historyMessages = [...res.records].reverse();

        if (isInitial) {
          setMessages(historyMessages);
          // 标记初始加载后滚动到底部，显示最新消息
          shouldScrollToBottomRef.current = true;
        } else {
          // 分页加载：将更早的历史消息插入到列表头部，去重
          setMessages(prev => {
            const existingIds = new Set(prev.map(msg => msg.id));
            const uniqueNew = historyMessages.filter(msg => !existingIds.has(msg.id));
            return [...uniqueNew, ...prev];
          });
        }

        setHistoryPage(page);
        setHasMoreHistory(page * 20 < (res.total || 0));
      }
    } catch {
      pendingScrollRestoreRef.current = null;
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [humanChatId]);

  /** 页面加载时拉取历史（StrictMode 防护） */
  useEffect(() => {
    if (initialLoadedRef.current) return;
    initialLoadedRef.current = true;
    loadHistory(1, true);
  }, [loadHistory]);

  /** 修复 iOS Safari 100vh 问题：动态设置 --app-height */
  useEffect(() => {
    const setAppHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    return () => window.removeEventListener('resize', setAppHeight);
  }, []);

  /** 消息更新后智能滚动处理（使用 useLayoutEffect 在浏览器绘制前完成定位，避免视觉闪烁） */
  useLayoutEffect(() => {
    if (messages.length === 0) return;
    const container = mainRef.current;
    if (!container) return;

    // 分页加载历史后，恢复滚动位置（保持用户浏览位置不跳动）
    if (pendingScrollRestoreRef.current) {
      const { scrollHeight: prevScrollHeight, scrollTop: prevScrollTop } = pendingScrollRestoreRef.current;
      pendingScrollRestoreRef.current = null;
      const addedHeight = container.scrollHeight - prevScrollHeight;
      container.scrollTop = prevScrollTop + addedHeight;
      return;
    }

    // 初始加载或新消息到达后，滚动到底部
    if (shouldScrollToBottomRef.current) {
      shouldScrollToBottomRef.current = false;
      container.scrollTop = container.scrollHeight;
      return;
    }
  }, [messages]);

  /** 监听滚动事件 - 向上滑到顶部时加载更早历史消息 */
  useEffect(() => {
    const container = mainRef.current;
    if (!container) return;

    let isLoadingLocked = false;
    const loadThreshold = 50; // 距离顶部多少像素内触发加载

    const handleScroll = () => {
      if (
        container.scrollTop < loadThreshold &&
        hasMoreHistoryRef.current &&
        !loadingMoreRef.current &&
        !isLoadingLocked &&
        !loadingRef.current
      ) {
        isLoadingLocked = true;
        loadHistory(historyPage + 1, false).finally(() => {
          setTimeout(() => {
            isLoadingLocked = false;
          }, 300);
        });
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [loadHistory, historyPage]);

  /** 聚焦状态变化后自动聚焦输入框（修复条件渲染切换textarea导致焦点丢失） */
  useEffect(() => {
    if (isFocused) {
      textareaRef.current?.focus();
    }
  }, [isFocused]);

  /** textarea自动调整高度（最多4行），放大模式下由flex控制 */
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    if (isExpanded) {
      textarea.style.height = 'auto';
      textarea.style.overflowY = 'auto';
      return;
    }

    textarea.style.height = 'auto';
    const lineHeight = 20;
    const maxHeight = lineHeight * 4;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [isExpanded]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputText, adjustTextareaHeight]);

  /** 发送消息（文本+附件，通过WebSocket） */
  const handleSend = () => {
    const text = inputText.trim();
    if (!text && attachments.length === 0) return;
    if (sending || uploading) return;
    const hasUploading = attachments.some((att) => att.uploading);
    if (hasUploading) return;

    setSending(true);

    // 构建multipart内容
    const parts: Array<{ type: string; text?: string; image_url?: { url: string }; video_url?: { url: string } }> = [];
    attachments.forEach((att) => {
      if (att.s3Url) {
        if (att.type === 'video') {
          parts.push({ type: 'video_url', video_url: { url: att.s3Url } });
        } else {
          parts.push({ type: 'image_url', image_url: { url: att.s3Url } });
        }
      }
    });
    if (text) {
      parts.push({ type: 'text', text });
    }

    const hasMedia = attachments.some((att) => att.type === 'image' || att.type === 'video');
    const contentType: 'text' | 'multipart' = hasMedia ? 'multipart' : 'text';
    const partsObj = hasMedia ? parts : null;

    // 本地消息（先标记为 sending 状态）
    const staffMsg: ChatLogItem = {
      id: `local-${Date.now()}`,
      chatId: '',
      role: 'staff',
      sysUserId: currentSysUserId,
      staffNickname: '我',
      isCurrentStaff: true,
      contentType,
      content: text,
      parts: hasMedia ? JSON.stringify(parts) : null,
      createTime: Date.now() / 1000,
      sendStatus: 'sending',
    };

    // 保存重试数据
    const retryData = { text, parts: partsObj, contentType };
    retryDataMapRef.current.set(staffMsg.id, retryData);

    setMessages((prev) => [...prev, staffMsg]);
    shouldScrollToBottomRef.current = true;

    try {
      // 检查网络状态，离线时立即标记为发送失败
      if (!navigator.onLine) throw new Error('Network is offline');
      ws.sendStaffMsg(humanChatId, contentType, text || null, partsObj);
      // 发送成功，标记为 sent
      setMessages((prev) => prev.map((msg) =>
        msg.id === staffMsg.id ? { ...msg, sendStatus: 'sent' as const } : msg
      ));
    } catch (error) {
      // 发送失败，标记为 failed 并持久化
      const failedMessage = { ...staffMsg, sendStatus: 'failed' as const };
      setMessages((prev) => prev.map((msg) =>
        msg.id === staffMsg.id ? failedMessage : msg
      ));

      console.error('[客服发送] 消息发送失败:', error);
    } finally {
      setInputText('');
      setAttachments([]);
      setSending(false);
      setIsExpanded(false);
    }
  };

  /** 重试发送失败的消息 */
  const handleRetry = useCallback((messageId: string) => {
    const retryData = retryDataMapRef.current.get(messageId);
    if (!retryData) return;

    setRetryingMessageId(messageId);
    // 标记消息为 sending 状态
    setMessages((prev) => prev.map((msg) =>
      msg.id === messageId ? { ...msg, sendStatus: 'sending' as const } : msg
    ));

    const { text, parts, contentType } = retryData;

    try {
      // 检查网络状态，离线时立即标记为重试失败
      if (!navigator.onLine) throw new Error('Network is offline');
      ws.sendStaffMsg(humanChatId, contentType, text || null, parts);
      // 重试成功
      setMessages((prev) => prev.map((msg) =>
        msg.id === messageId ? { ...msg, sendStatus: 'sent' as const } : msg
      ));

    } catch (error) {
      // 重试失败
      setMessages((prev) => prev.map((msg) =>
        msg.id === messageId ? { ...msg, sendStatus: 'failed' as const } : msg
      ));
      console.error('[重试] 客服消息发送失败:', error);
    } finally {
      setRetryingMessageId(null);
    }
  }, [humanChatId, ws]);

  /** 显示结束会话确认对话框 */
  const handleShowEndChatConfirm = () => {
    setShowDropdown(false);
    setShowEndChatConfirm(true);
  };

  /** 取消结束会话 */
  const handleCancelEndChat = () => {
    setShowEndChatConfirm(false);
  };

  /** 确认结束会话 */
  const handleConfirmEndChat = async () => {
    setShowEndChatConfirm(false);
    if (!humanChatId) return;
    try {
      await closeChatApi({ humanChatId });
      ws.sendStaffClose(humanChatId);
      // 清空失败消息持久化数据

      retryDataMapRef.current.clear();
      handleBack();
    } catch {
      // 关闭失败忽略
    }
  };

  /** 结束会话（旧方法，保留兼容） */
  const handleEndChat = async () => {
    setShowDropdown(false);
    if (!humanChatId) return;
    try {
      await closeChatApi({ humanChatId });
      ws.sendStaffClose(humanChatId);
      handleBack();
    } catch {
      // 关闭失败忽略
    }
  };

  // ==================== 文件上传（图片+视频） ====================

  /** 检测文件 MIME 类型 */
  function getFileType(file: File): 'image' | 'video' {
    return file.type.startsWith('video/') ? 'video' : 'image';
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    // 无论成功、超限还是异常都要重置 value，否则再次选择同一文件不会触发 change
    const resetInput = () => { e.target.value = ''; };

    // 同步互斥：上一次选择的文件可能仍在压缩/上传中（尚未全部计入 attachments 快照），
    // 此时再选会按旧数量计算剩余配额，导致总数突破上限；用 ref 判定不依赖 React 渲染提交时机
    if (uploadingRef.current) {
      showToast('文件处理中，请稍候');
      resetInput();
      return;
    }

    // 限制图片+视频总数最多 MAX_IMAGE_COUNT 个
    const currentMediaCount = countMediaAttachments(attachments);
    const remainingSlots = MAX_IMAGE_COUNT - currentMediaCount;
    if (remainingSlots <= 0) {
      showToast(`最多可上传${MAX_IMAGE_COUNT}张图片或视频`);
      resetInput();
      return;
    }

    // 分类：图片需要压缩，视频直接上传
    const allFiles = Array.from(selectedFiles);
    const imageFiles = allFiles.filter(f => getFileType(f) === 'image');
    const videoFiles = allFiles.filter(f => getFileType(f) === 'video');

    // 图片+视频总数超过剩余配额时提示
    const totalNewMedia = imageFiles.length + videoFiles.length;
    if (totalNewMedia > remainingSlots) {
      showToast(`最多可上传${MAX_IMAGE_COUNT}张图片或视频`);
    }
    // 图片和视频都截取到剩余配额内（优先保留图片）
    const imagesToProcess = imageFiles.slice(0, remainingSlots);
    const remainingAfterImages = remainingSlots - imagesToProcess.length;
    const videosToProcess = videoFiles.slice(0, remainingAfterImages);
    const filesToProcess = [...imagesToProcess, ...videosToProcess];

    uploadingRef.current = true;
    setUploading(true);
    // 压缩阶段在 try 内执行：任意一个文件压缩抛异常都不能卡住上传态，统一由 finally 释放
    try {
      for (const file of filesToProcess) {
        const fileType = getFileType(file);
        // 图片先压缩，视频也压缩
        const processedFile = fileType === 'image'
          ? await compressImage(file)
          : await compressVideo(file);

        const localUrl = URL.createObjectURL(processedFile);
        const id = generateId();

        setAttachments((prev) => [
          ...prev,
          { id, type: fileType, url: localUrl, name: processedFile.name, uploading: true },
        ]);

        try {
          const s3Url = await uploadCsFile(processedFile);
          if (!s3Url) throw new Error(`上传失败: ${file.name}`);
          setAttachments((prev) =>
            prev.map((att) => (att.id === id ? { ...att, s3Url, uploading: false } : att))
          );
        } catch {
          setAttachments((prev) => prev.filter((att) => att.id !== id));
          URL.revokeObjectURL(localUrl);
        }
      }
    } finally {
      setUploading(false);
      uploadingRef.current = false;
      resetInput();
    }
  };

  const removeAttachment = (id: string) => {
    const index = attachments.findIndex((a) => a.id === id);
    if (index !== -1) {
      URL.revokeObjectURL(attachments[index].url);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    }
  };

  // ==================== 文字输入 ====================

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_CHAR_LIMIT) {
      setInputText(value);
    } else {
      showToast('输入内容已达上限');
    }
  };

  // ==================== 表情选择 ====================

  const COMMON_EMOJIS = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
    '🙂', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗',
    '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭',
    '🤔', '🤐', '😐', '😑', '😶', '😏', '😒', '🙄',
    '😬', '😮', '😯', '😲', '😳', '🥺', '😢', '😭',
    '😤', '😡', '🤬', '😈', '👿', '💀', '☠️', '💩',
    '👍', '👎', '👊', '✊', '🤛', '🤜', '👏', '🙌',
    '👐', '🤲', '🤝', '🙏', '✌️', '🤞', '💅', '👀',
  ];

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev);
  };

  const insertEmoji = (emoji: string) => {
    setInputText((prev) => prev + emoji);
    // 插入表情后重新聚焦输入框
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  /** 渲染单条消息 */
  const renderMessage = (msg: ChatLogItem, showTime: boolean = true) => {
    // 分割线消息（会话边界标记）
    if (msg.role === 'system' && (msg.displayType || 'system_tip') === 'split') {
      return (
        <div key={msg.id} className={styles.msgSplitLine}>
          <div className={styles.msgSplitLineInner} />
          <span className={styles.msgSplitLineText}>{msg.content}</span>
          <div className={styles.msgSplitLineInner} />
        </div>
      );
    }

    const isStaffSide = msg.role === 'staff' || msg.role === 'assistant' || msg.role === 'system';
    const readStatus = getReadStatus(msg, lastReadTime);

    // ==================== 用户消息（靠左，浅灰背景） ====================
    if (msg.role === 'user') {
      return (
        <div key={msg.id} className={`${styles.msgRow} ${styles.msgRowLeft}`}>
          {/* 用户头像：优先使用消息自带快照，降级用路由传入的 userAvatar */}
          {(msg.userAvatar || userAvatar)
            ? <img src={msg.userAvatar || userAvatar} alt="" className={styles.msgAvatar} />
            : <div className={styles.msgAvatarPlaceholder}>用</div>
          }
          {/* 内容列 */}
          <div className={`${styles.msgContent} ${styles.msgContentLeft}`}>
            {/* 消息卡片 */}
            <div className={`${styles.msgCard} ${styles.msgCardLeft}`}>
              {msg.contentType === 'text' && <span>{msg.content}</span>}
              {msg.contentType === 'multipart' && (
                <div>{renderMultipartContent(msg.parts, msg.content, (url, type) => setPreviewFile({ url, type }))}</div>
              )}
            </div>
            {/* 时间（1小时间隔规则：同角色连续多条消息仅首条显示） */}
            {showTime && (
              <span className={`${styles.msgTime} ${styles.msgTimeLeft}`}>
                {formatTime(msg.createTime)}
              </span>
            )}
          </div>
        </div>
      );
    }

    // ==================== 客服/AI消息（靠右，蓝色背景） ====================
    if (isStaffSide) {
      // AI(assistant/system)消息：不使用后端下发的 AI_ASSISTANT_AVATAR S3 URL，直接默认用默认 AI 助手 logo
      // 人工客服(staff)消息：优先使用消息自带头像，降级用当前登录客服头像，再降级用默认客服头像
      const avatarSrc = msg.role === 'assistant' || msg.role === 'system'
        ? defaultAssistantLogo
        : (msg.staffAvatar || staffAvatar || defaultAvatar);
      const isSendFailed = msg.sendStatus === 'failed';
      const isRetrying = retryingMessageId === msg.id;
      return (
        <div key={msg.id} className={`${styles.msgRow} ${styles.msgRowRight}`}>
          {/* 内容列 */}
          <div className={`${styles.msgContent} ${styles.msgContentRight}`}>
            {/* 气泡行（发送失败图标 + 消息卡片在同一水平居中） */}
            <div className={styles.msgBubbleRow}>
              {isSendFailed && (
                <div className={styles.sendFailedWrap}>
                  <img
                    src={sendFailedIcon}
                    alt="重试"
                    className={styles.sendFailedIcon}
                    style={{
                      cursor: isRetrying ? 'wait' : 'pointer',
                      opacity: isRetrying ? 0.5 : 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isRetrying) handleRetry(msg.id);
                    }}
                  />
                </div>
              )}
              {/* 消息卡片 */}
              <div className={`${styles.msgCard} ${styles.msgCardRight}`}>
                {msg.contentType === 'text' && <span>{msg.content}</span>}
                {msg.contentType === 'multipart' && (
                  <div>{renderMultipartContent(msg.parts, msg.content, (url, type) => setPreviewFile({ url, type }))}</div>
                )}
              </div>
            </div>
            {/* 时间 + 已读/未读（仅当前客服自己发的消息） */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {readStatus !== 'none' && (
                <span className={readStatus === 'read' ? styles.msgReadTag : styles.msgUnreadTag}>
                  {readStatus === 'read' ? '已读' : '未读'}
                </span>
              )}
              {showTime && (
                <span className={`${styles.msgTime} ${styles.msgTimeRight}`}>
                  {formatTime(msg.createTime)}
                </span>
              )}
            </div>
          </div>
          {/* 客服头像 */}
          {avatarSrc
            ? <img src={avatarSrc} alt="" className={styles.msgAvatar} />
            : <div className={styles.msgAvatarPlaceholder}>客</div>
          }
        </div>
      );
    }

    return null;
  };

  /** 渲染消息列表（插入时间分隔条 + 应用1小时间隔规则） */
  const renderMessageList = () => {
    const elements: JSX.Element[] = [];
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const prevMsg = i > 0 ? messages[i - 1] : null;
      const isSplitMsg = msg.role === 'system' && (msg.displayType || 'system_tip') === 'split';

      // 时间分隔条：首条消息或日期变化时显示（跳过分割线类型消息）
      if (!isSplitMsg && (i === 0 || (prevMsg && !isSameDay(prevMsg.createTime, msg.createTime)))) {
        elements.push(
          <div key={`ts-${i}`} className={styles.timeSeparator}>
            <span className={styles.timeSeparatorText}>{getTimeSeparatorText(msg.createTime)}</span>
          </div>
        );
      }

      // 1小时间隔规则：同角色多消息连续且间隔在1小时内，仅首条显示时间
      let showTime = true;
      if (prevMsg && !isSplitMsg && msg.role === prevMsg.role) {
        showTime = isOverOneHour(msg.createTime, prevMsg.createTime);
      }

      elements.push(renderMessage(msg, showTime));
    }
    return elements;
  };

  const hasContent = inputText.trim().length > 0 || attachments.length > 0;
  const isSendDisabled = sending || uploading || (!inputText.trim() && attachments.length === 0) || attachments.some((a) => a.uploading);
  /** 附件配额已满或正在处理文件：置灰“+”入口，避免用户打开系统选择器后才发现超限 */
  const isAddDisabled = fromTab === 'history' || uploading || countMediaAttachments(attachments) >= MAX_IMAGE_COUNT;

  return (
    <div className={`${styles.container} ${isExpanded ? styles.containerExpanded : ''}`}>
      {/* ==================== 顶部导航 ==================== */}
      <div className={styles.header}>
        {/* 左侧：返回图标 + 用户信息 */}
        <div className={styles.headerLeft}>
          <button type="button" className={styles.backBtn} onClick={handleBack}>
            <img src={backIcon} alt="返回" />
          </button>
          <div className={styles.userInfo}>
            {userAvatar
              ? <img src={userAvatar} alt="" className={styles.userAvatar} />
              : <div className={styles.userAvatarPlaceholder}>用</div>
            }
            <div className={styles.userDetail}>
              <span className={styles.userName}>{userNickname}</span>
              {fromTab !== 'history' && (
                <div className={styles.onlineStatus}>
                  <span className={styles.onlineDot} />
                  <span className={styles.onlineText}>在线</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* 右侧：更多图标（下拉菜单含结束会话，仅进行中会话显示） */}
        {chatStatus !== 3 && (
          <div className={styles.headerRight}>
            <div
              className={styles.moreBtn}
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <MoreVertical size={18} />
              {showDropdown && (
                <div className={styles.moreDropdown}>
                  <button
                    type="button"
                    className={`${styles.moreDropdownItem} ${styles.moreDropdownItemDanger}`}
                    onClick={handleShowEndChatConfirm}
                  >
                    结束会话
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ==================== 主体消息区 ==================== */}
      <div ref={mainRef} className={`${styles.main} ${isExpanded ? styles.mainExpanded : ''}`}>
        {loading ? (
          <div className={styles.loadingWrap}>加载中...</div>
        ) : (
          <div className={styles.msgWrap}>
            {loadingMore && (
              <div className={styles.loadingMore}>加载中...</div>
            )}
            {!hasMoreHistory && messages.length > 0 && (
              <div className={styles.noMoreData}>没有更多消息了</div>
            )}
            {renderMessageList()}
          </div>
        )}
      </div>

      {/* ==================== 底部输入区 ==================== */}
      <div className={`${styles.chatInputContainer} ${isExpanded ? styles.chatInputContainerExpanded : ''}`}>
        <div className={`${styles.chatInputBox} ${isExpanded ? styles.chatInputBoxExpanded : ''}`}>
          {/* ----- 初始状态：无内容且未聚焦，一行显示 ----- */}
          {!hasContent && !isFocused ? (
            <div className={styles.initialRow} onClick={fromTab !== 'history' ? () => textareaRef.current?.focus() : undefined}>
              <textarea
                ref={textareaRef}
                className={styles.chatTextarea}
                placeholder={fromTab === 'history' ? '历史会话' : '请输入'}
                value={inputText}
                onChange={handleTextChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (fromTab !== 'history') handleSend();
                  }
                }}
                onFocus={() => fromTab !== 'history' && setIsFocused(true)}
                disabled={fromTab === 'history'}
                rows={1}
              />
              <div className={styles.initialRowActions}>
                <button
                  type="button"
                  className={styles.chatIconBtn}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (fromTab !== 'history') fileInputRef.current?.click();
                  }}
                  disabled={isAddDisabled}
                >
                  <img src={chatAddIcon} alt="添加" />
                </button>
                <button
                  type="button"
                  className={styles.chatIconBtn}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (fromTab !== 'history') toggleEmojiPicker();
                  }}
                  disabled={fromTab === 'history'}
                >
                  <img src={chatEmojiIcon} alt="表情" />
                </button>
              </div>
            </div>
          ) : (
            /* ----- 有内容或聚焦状态：附件预览 + textarea + 工具栏 ----- */
            <>
              {/* 图片附件预览 */}
              {attachments.length > 0 && (
                <div className={styles.attachmentsPreview}>
                  {attachments.map((att) => (
                    <div key={att.id} className={styles.attachmentItem}>
                      {att.type === 'video' ? (
                        <div className={styles.attachmentVideoItem} onClick={() => setPreviewFile({ url: att.url, type: 'video' })}>
                          <video
                            src={`${att.url}#t=0.1`}
                            className={styles.attachmentThumb}
                            preload="auto"
                            muted
                            playsInline
                            onLoadedMetadata={(e) => { e.currentTarget.currentTime = 0.1; }}
                          />
                          <div className={styles.playIconOverlay}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><polygon points="8,5 19,12 8,19" /></svg>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={att.url}
                          alt={att.name}
                          className={styles.attachmentThumb}
                          onClick={() => setPreviewFile({ url: att.url, type: 'image' })}
                        />
                      )}
                      <button
                        className={styles.attachmentRemove}
                        onClick={(e) => { e.stopPropagation(); removeAttachment(att.id); }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* 文本输入区域 */}
              <textarea
                ref={textareaRef}
                className={`${styles.chatTextarea} ${isExpanded ? styles.chatTextareaExpanded : ''}`}
                placeholder="请输入"
                value={inputText}
                onChange={handleTextChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (fromTab !== 'history') handleSend();
                  }
                }}
                onFocus={() => fromTab !== 'history' && setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                disabled={fromTab === 'history'}
                rows={1}
              />

              {/* 底部工具栏 */}
              <div className={styles.inputToolbar}>
                <div className={styles.inputToolbarLeft}>
                  <button
                    type="button"
                    className={styles.inputActionBtn}
                    onClick={() => setIsExpanded(!isExpanded)}
                    disabled={fromTab === 'history'}
                  >
                    {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                  </button>
                </div>
                <div className={styles.inputToolbarRight}>
                  {/*  移除了底部工具栏的字符计数显示（text.length/4000 / inputText.length/4000*/}
                  {/*  <span className={styles.charCount}>*/}
                  {/*  {inputText.length}/{MAX_CHAR_LIMIT}*/}
                  {/*</span>*/}
                  <button
                    type="button"
                    className={styles.inputActionBtn}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isAddDisabled}
                  >
                    <img src={chatAddIcon} alt="添加" />
                  </button>
                  <button
                    type="button"
                    className={styles.inputActionBtn}
                    onClick={toggleEmojiPicker}
                    disabled={fromTab === 'history'}
                  >
                    <img src={chatEmojiIcon} alt="表情" />
                  </button>
                  {hasContent && fromTab !== 'history' && (
                    <button
                      type="button"
                      className={styles.sendBtn}
                      onClick={handleSend}
                      disabled={isSendDisabled}
                    >
                      <img src={sendIcon} alt="发送" />
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

          {/* 隐藏的文件选择器 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* 表情选择器 */}
          {showEmojiPicker && (
            <div className={styles.emojiPickerOverlay}>
              <div className={styles.emojiPickerHeader}>
                <span className={styles.emojiPickerTitle}>选择表情</span>
                <button
                  type="button"
                  className={styles.emojiPickerClose}
                  onClick={() => setShowEmojiPicker(false)}
                >
                  <X size={18} />
                </button>
              </div>
              <div className={styles.emojiPickerGrid}>
                {COMMON_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className={styles.emojiItem}
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* 全屏预览浮层 */}
          {previewFile && (
            <div className={styles.previewOverlay} onClick={() => setPreviewFile(null)}>
              <button
                type="button"
                className={styles.previewCloseBtn}
                onClick={() => setPreviewFile(null)}
              >
                <X size={24} />
              </button>
              {previewFile.type === 'image' ? (
                <img
                  src={previewFile.url}
                  alt="预览"
                  className={styles.previewMedia}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <video
                  src={previewFile.url}
                  className={styles.previewMedia}
                  controls
                  autoPlay
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
          )}

          {/* 结束会话确认对话框 */}
          {showEndChatConfirm && (
            <div className={styles.confirmOverlay} onClick={handleCancelEndChat}>
              <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
                <div className={styles.confirmTitle}>是否结束会话？</div>
                <div className={styles.confirmButtons}>
                  <button
                    type="button"
                    className={styles.confirmBtnCancel}
                    onClick={handleCancelEndChat}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className={styles.confirmBtnConfirm}
                    onClick={handleConfirmEndChat}
                  >
                    确定
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Toast 提示 */}
          {toastMsg && (
            <div className={styles.toastFloating}>{toastMsg}</div>
          )}
        </div>
    </div>
  );
}
